import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { X, Check, SkipForward, Sparkles, Loader2 } from 'lucide-react';
import { useQuery } from 'react-query';
import type { DetectedPage, DatasheetFrontmatter } from '@/services/datasheetService';
import { getPartTypes, type PartTypeOption } from '@/services/bomService';
import { usePartCodes } from '@/hooks/usePSI';

interface FootprintBBoxApprovalProps {
  detectedPages: DetectedPage[];
  sourceFilename: string;
  frontmatter?: DatasheetFrontmatter | null;
  onApprove: (pageIndex: number, bbox: { x1: number; y1: number; x2: number; y2: number }, userHint?: string, typeCode?: string, partNumber?: string) => void;
  onSkip: () => void;
  onBack: () => void;
  onCancel: () => void;
}

const FootprintBBoxApproval: React.FC<FootprintBBoxApprovalProps> = ({
  detectedPages, sourceFilename, frontmatter, onApprove, onSkip, onBack, onCancel,
}) => {
  const [selectedPageIdx, setSelectedPageIdx] = useState(0);
  const [bbox, setBbox] = useState<{ x1: number; y1: number; x2: number; y2: number }>(
    detectedPages[0]?.bbox ?? { x1: 5, y1: 5, x2: 95, y2: 95 }
  );
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawStart, setDrawStart] = useState<{ x: number; y: number } | null>(null);
  const [drawCurrent, setDrawCurrent] = useState<{ x: number; y: number } | null>(null);
  const [userHint, setUserHint] = useState('');
  const [selectedTypeCode, setSelectedTypeCode] = useState('');

  const overlayRef = useRef<HTMLDivElement>(null);

  // Fetch part types (電子零件別) and existing part codes for next number
  const { data: partTypes = [] } = useQuery<PartTypeOption[]>('partTypes', getPartTypes, { staleTime: 300000 });
  const { data: partCodes } = usePartCodes('electronic_parts');

  // Generate next consecutive 雲騰品號
  const nextPartNumber = useMemo(() => {
    if (!partCodes || !selectedTypeCode) return '';
    const prefix = `${selectedTypeCode}-`;
    const nums = partCodes
      .filter(p => p.part_number?.startsWith(prefix))
      .map(p => parseInt(p.part_number?.substring(prefix.length) || '', 10))
      .filter(n => !isNaN(n));
    const next = (nums.length > 0 ? Math.max(...nums) : 0) + 1;
    return `${selectedTypeCode}-${String(next).padStart(5, '0')}`;
  }, [partCodes, selectedTypeCode]);

  // Auto-match extracted category to part type when frontmatter arrives
  useEffect(() => {
    if (!frontmatter || selectedTypeCode || !partTypes.length) return;
    const raw = (frontmatter.component?.category || frontmatter.category || '').toLowerCase();
    if (!raw) return;
    // Try exact match first (e.g., "LED" → "LED")
    const exact = partTypes.find(pt => pt.part_code.toLowerCase() === raw);
    if (exact) { setSelectedTypeCode(exact.part_code); return; }
    // Try keyword match against English/Chinese names
    const keyword = partTypes.find(pt =>
      raw.includes(pt.part_type_en.toLowerCase()) || pt.part_type_en.toLowerCase().includes(raw) ||
      raw.includes(pt.part_type_zh) || raw.includes(pt.part_code.toLowerCase())
    );
    if (keyword) setSelectedTypeCode(keyword.part_code);
  }, [frontmatter, partTypes, selectedTypeCode]);

  const currentPage = detectedPages[selectedPageIdx];

  const handlePageSelect = (idx: number) => {
    setSelectedPageIdx(idx);
    setBbox(detectedPages[idx]?.bbox ?? { x1: 5, y1: 5, x2: 95, y2: 95 });
  };

  // Mouse events for redrawing bbox
  const getRelativePos = useCallback((e: React.MouseEvent) => {
    const rect = overlayRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    return {
      x: Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100)),
      y: Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100)),
    };
  }, []);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return;
    const pos = getRelativePos(e);
    setIsDrawing(true);
    setDrawStart(pos);
    setDrawCurrent(pos);
  }, [getRelativePos]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDrawing) return;
    setDrawCurrent(getRelativePos(e));
  }, [isDrawing, getRelativePos]);

  const handleMouseUp = useCallback(() => {
    if (!isDrawing || !drawStart || !drawCurrent) {
      setIsDrawing(false);
      return;
    }

    const x1 = Math.min(drawStart.x, drawCurrent.x);
    const y1 = Math.min(drawStart.y, drawCurrent.y);
    const x2 = Math.max(drawStart.x, drawCurrent.x);
    const y2 = Math.max(drawStart.y, drawCurrent.y);

    // Only update if box is big enough (at least 3% in each dimension)
    if (x2 - x1 > 3 && y2 - y1 > 3) {
      setBbox({ x1, y1, x2, y2 });
    }

    setIsDrawing(false);
    setDrawStart(null);
    setDrawCurrent(null);
  }, [isDrawing, drawStart, drawCurrent]);

  const handleApprove = () => {
    onApprove(selectedPageIdx, bbox, userHint.trim() || undefined, selectedTypeCode || undefined, nextPartNumber || undefined);
  };

  // Active bbox to display (use in-progress drawing if active)
  const displayBbox = isDrawing && drawStart && drawCurrent
    ? {
        x1: Math.min(drawStart.x, drawCurrent.x),
        y1: Math.min(drawStart.y, drawCurrent.y),
        x2: Math.max(drawStart.x, drawCurrent.x),
        y2: Math.max(drawStart.y, drawCurrent.y),
      }
    : bbox;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-6xl w-full h-[90vh] overflow-hidden m-4 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Footprint Detection
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {sourceFilename} — Verify the detected dimensional drawing area. Redraw to adjust.
            </p>
          </div>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Page tabs (if multiple) */}
        {detectedPages.length > 1 && (
          <div className="flex gap-1 px-4 pt-3">
            {detectedPages.map((p, i) => (
              <button
                key={i}
                onClick={() => handlePageSelect(i)}
                className={`px-3 py-1 text-xs rounded-full ${
                  i === selectedPageIdx
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                Page {p.page}
              </button>
            ))}
          </div>
        )}

        {/* Body — two-column layout */}
        <div className="flex-1 flex min-h-0 overflow-hidden">
          {/* Left: Image with bbox overlay */}
          <div className="flex-1 overflow-auto p-4">
            <div className="flex justify-center h-full">
              <div className="relative inline-block">
                {currentPage && (
                  <img
                    src={`data:image/png;base64,${currentPage.page_png_base64}`}
                    alt={`Page ${currentPage.page}`}
                    className="max-w-full max-h-full border border-gray-200 dark:border-gray-700 rounded"
                    draggable={false}
                  />
                )}

                {/* Overlay for bbox drawing */}
                <div
                  ref={overlayRef}
                  className="absolute inset-0 cursor-crosshair"
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseUp}
                >
                  {/* Semi-transparent mask outside bbox */}
                  <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute left-0 right-0 top-0 bg-black/30" style={{ height: `${displayBbox.y1}%` }} />
                    <div className="absolute left-0 right-0 bottom-0 bg-black/30" style={{ height: `${100 - displayBbox.y2}%` }} />
                    <div className="absolute left-0 bg-black/30" style={{ top: `${displayBbox.y1}%`, height: `${displayBbox.y2 - displayBbox.y1}%`, width: `${displayBbox.x1}%` }} />
                    <div className="absolute right-0 bg-black/30" style={{ top: `${displayBbox.y1}%`, height: `${displayBbox.y2 - displayBbox.y1}%`, width: `${100 - displayBbox.x2}%` }} />
                  </div>

                  {/* Bbox border */}
                  <div
                    className="absolute pointer-events-none"
                    style={{
                      left: `${displayBbox.x1}%`,
                      top: `${displayBbox.y1}%`,
                      width: `${displayBbox.x2 - displayBbox.x1}%`,
                      height: `${displayBbox.y2 - displayBbox.y1}%`,
                      border: '2px solid #3b82f6',
                      boxShadow: '0 0 0 1px rgba(59, 130, 246, 0.3)',
                    }}
                  >
                    <span className="absolute -top-5 left-0 text-[10px] font-medium px-1 rounded bg-blue-600 text-white whitespace-nowrap">
                      Dimensional Drawing
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Part info + AI hint */}
          <div className="w-[280px] shrink-0 border-l border-gray-200 dark:border-gray-700 flex flex-col overflow-auto">
            {/* Part type (類別) + auto-generated 雲騰品號 */}
            <div className="p-3 space-y-2 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Part Code
              </h3>
              <div>
                <label className="text-[10px] text-gray-400 dark:text-gray-500">
                  類別 <span className="text-red-400">*</span>
                </label>
                <select
                  value={selectedTypeCode}
                  onChange={e => setSelectedTypeCode(e.target.value)}
                  className="w-full mt-0.5 px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  <option value="">Select...</option>
                  {partTypes.map(pt => (
                    <option key={pt.part_code} value={pt.part_code}>
                      {pt.part_code} - {pt.part_type_zh}
                    </option>
                  ))}
                </select>
              </div>
              {selectedTypeCode && (
                <div>
                  <span className="text-[10px] text-gray-400 dark:text-gray-500">雲騰品號</span>
                  <p className="text-xs font-mono text-green-600 dark:text-green-400">
                    {nextPartNumber || <Loader2 className="w-3 h-3 animate-spin inline-block" />}
                  </p>
                </div>
              )}
            </div>

            {/* Extracted fields (from early text extraction) */}
            <div className="p-3 space-y-2 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Extracted Info
                {!frontmatter && <Loader2 className="w-3 h-3 animate-spin inline-block ml-1.5" />}
              </h3>
              {frontmatter ? (
                <>
                  <ExtractedField label="Brand" value={frontmatter.component?.manufacturer || frontmatter.manufacturer} />
                  <ExtractedField label="Part Number" value={frontmatter.component?.part_number || frontmatter.part_number} />
                  <ExtractedField label="Package" value={frontmatter.component?.package || frontmatter.package} />
                  <ExtractedField label="Description" value={frontmatter.description} />
                </>
              ) : (
                <p className="text-[10px] text-gray-400 dark:text-gray-500 italic">
                  Extracting datasheet info...
                </p>
              )}
            </div>

            {/* AI hint textarea */}
            <div className="p-3 flex-1 flex flex-col">
              <div className="flex items-center gap-1.5 mb-2">
                <Sparkles className="w-3.5 h-3.5 text-purple-400" />
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400">AI Hint</span>
              </div>
              <textarea
                value={userHint}
                onChange={e => setUserHint(e.target.value)}
                placeholder="Optional: describe the component for better extraction (e.g., 'QFN-16 package, 3.5x3.5mm body, 0.5mm pitch')"
                className="flex-1 min-h-[60px] px-3 py-2 text-xs border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 resize-none"
              />
              <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1.5">
                Click and drag on the image to redraw the detection area.
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="shrink-0 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between gap-3 p-4">
            <button
              onClick={onBack}
              className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
            >
              Back
            </button>
            <div className="flex gap-2">
              <button
                onClick={onSkip}
                className="inline-flex items-center gap-1 px-3 py-2 text-xs text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                <SkipForward className="w-3 h-3" />
                Skip Footprint
              </button>
              <button
                onClick={handleApprove}
                className="inline-flex items-center gap-1 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <Check className="w-4 h-4" />
                Approve & Extract
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ExtractedField: React.FC<{ label: string; value?: string }> = ({ label, value }) => (
  <div>
    <span className="text-[10px] text-gray-400 dark:text-gray-500">{label}</span>
    <p className={`text-xs truncate ${value ? 'text-gray-800 dark:text-gray-200' : 'text-gray-400 dark:text-gray-600 italic'}`}>
      {value || '—'}
    </p>
  </div>
);

export default FootprintBBoxApproval;

